// deps
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

// internal-deps
import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { AutoPayFailedOrderActionPayload, ConfirmOrderActionPayload } from "@swiggy-private/checkout-core";
import { AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-types";

// constants
import { AUTOPAY_ASSETS, TEXTS, TIME_INTERVALS } from "../constants";

// components
import ExclamationSvgImage from "./exclamation/consent/Exclamation";
import ConsentVariantButtons from "./ConsentVariantButtons";
import { Shimmer } from "./shimmer";
import { AutopayTimelineContent } from "./autopay-timeline-content";
import { AutopayTickScreen } from "./tick-screen";

// styles
import styles from "../styles.scss";
import { ImageContainer, SmileyImage } from "./styles";

export interface Props {
    payload: AutoPayFailedOrderActionPayload;
    goToTrackScreen: () => void;
    handleSecondaryCtaClick: () => void;
    handlePrimaryCtaClick: () => void;
    codData: PaymentMethod<PaymentMethodMeta> | undefined;
    successPayload?: ConfirmOrderActionPayload;
    failureReason?: string;
}

export const AutopayPaymentFailureScreenContent = memo(function AutopayPaymentFailureScreenContent(
    props: Props,
): ReactElement {
    const [showShimmer, setShimmer] = useState(false);
    const [startExclamationAnimation, setExclamationAnimation] = useState(false);
    const [showMainContent, setShowMainContent] = useState(false);
    const [showTimelineContentWithCta, setTimelineContent] = useState(false);

    const exclamationTimeout = useRef<NodeJS.Timeout | null>(null);
    const mainContentTimeout = useRef<NodeJS.Timeout | null>(null);
    const timelineTimeout = useRef<NodeJS.Timeout | null>(null);

    const { failureReasonSubText, autopayTimelineText, autopayCta } = props.payload?.paymentFailureResponse || {};
    const { isIrctcRegularOrder } = props.payload?.failedOrder || {};

    const handlePrimaryCtaClick = useCallback(() => {
        setShimmer(true);
        props.handlePrimaryCtaClick();
    }, [props]);

    useEffectOnce(() => {
        exclamationTimeout.current = setTimeout(() => {
            setExclamationAnimation(true);
        }, TIME_INTERVALS.CONSENT.START);

        // Show main content once the other css animation on page is finished
        mainContentTimeout.current = setTimeout(() => {
            setShowMainContent(true);
        }, TIME_INTERVALS.CONSENT.MAIN_CONTENT_SHOW);

        timelineTimeout.current = setTimeout(() => {
            setTimelineContent(true);
        }, TIME_INTERVALS.CONSENT.SHOW_TIMELINE);
    });

    const exclamationContainerClassname = useMemo(() => {
        return classNames(styles.exclamationContainerFullpage, {
            [styles.exclamationContainerFullpageAnimate]: startExclamationAnimation,
        });
    }, [startExclamationAnimation]);

    const mainContentClassName = useMemo(() => {
        return classNames(styles.mainContainerWrapper, {
            [styles.mainContainerWrapperWithAnimation]: showMainContent,
        });
    }, [showMainContent]);

    const renderMainContent = useCallback((): ReactElement => {
        return (
            <div className={mainContentClassName} data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.LOTTIE_WRAPPER}>
                <div
                    className={styles.mainContainerTitle}
                    data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.LOTTIE_TITLE}
                >
                    {failureReasonSubText || TEXTS.CONSENT.TITLE}
                    <ImageContainer>
                        <SmileyImage cloudinaryId={AUTOPAY_ASSETS.MAIN_TEXT_IMG} />
                    </ImageContainer>
                </div>
            </div>
        );
    }, [mainContentClassName, failureReasonSubText]);

    const failureTitle = props?.failureReason || TEXTS.ERROR_TITLE;

    useEffect(() => {
        // only need to clear timeout when component unmounts
        return () => {
            exclamationTimeout.current && clearTimeout(exclamationTimeout.current);
            mainContentTimeout.current && clearTimeout(mainContentTimeout.current);
            timelineTimeout.current && clearTimeout(timelineTimeout.current);
        };
    }, []);

    const autopayTimelineData = useMemo(() => {
        if (!isEmpty(autopayTimelineText)) {
            return autopayTimelineText;
        }
        if (isIrctcRegularOrder) {
            return {
                next: TEXTS.TIMELINE.IRCTC_NEXT_STATE_TEXT,
                nextAlt: TEXTS.TIMELINE.IRCTC_NEXT_STATE_ALT_TEXT,
            };
        }
    }, [isIrctcRegularOrder, autopayTimelineText]);

    if (!isEmpty(props.successPayload)) {
        return <AutopayTickScreen goToTrackScreen={props.goToTrackScreen} />;
    }

    if (showShimmer) {
        return <Shimmer />;
    }

    return (
        <div className={styles.wrapper}>
            <div className={exclamationContainerClassname}>
                <ExclamationSvgImage className={styles.exclamationSvg} />
                <div
                    className={styles.exclamationTitle}
                    data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.ERROR_TITLE}
                >
                    {failureTitle}
                </div>
                {renderMainContent()}
            </div>

            <AutopayTimelineContent show={showTimelineContentWithCta} timelineText={autopayTimelineData} />
            {showTimelineContentWithCta && (
                <div className={styles.ctaWrapper}>
                    <ConsentVariantButtons
                        cta={autopayCta}
                        handleSecondaryCtaClick={props.handleSecondaryCtaClick}
                        handlePrimaryCtaClick={handlePrimaryCtaClick}
                    />
                </div>
            )}
        </div>
    );
});
